import { User } from '@huspy/briks-icons';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from '@huspy/briks-web';
import { SectionHeader } from '@modules/opportunities/presentation/pages/CaseCreation/components/CaseInputs';
import {
  FormInput,
  FormSelectInput
} from '@huspy/forge';
import {
  NATIONALITIES,
  getEmploymentType,
  getResidencyType,
  getBonusTypes,
  getYesNo,
} from '@modules/opportunities/presentation/pages/const';
import { OPPORTUNITY_CURRENCY } from '@modules/opportunities/entities/opportunity/const';
import { UseFormReturnType } from '@mantine/form';
import { BusinessIdentifier } from '@modules/core/api/types';
import { BUSINESS_IDENTIFIER } from '@modules/core/api/const';
import Radio from '@components/v2/Radio';

export const MainApplicantInfo = ({
  form,
  isSomeCasesSubmitted,
  businessUnit,
}: {
  form: UseFormReturnType<any>;
  isSomeCasesSubmitted: boolean;
  businessUnit: BusinessIdentifier
}) => {
  const { t } = useTranslation();
  return (
    <>
      <SectionHeader
        title={ t('clients.formSections.mainApplicantInfo') }
        Icon={ User }
      />
      <Flex gap='4'>
        <FormInput
          name='first_name'
          label={ t('clients.formLabels.firstName') }
          data-test='input-firstname'
          form={ form }
          disabled={ isSomeCasesSubmitted }
        />
        <FormInput
          name='last_name'
          label={ t('clients.formLabels.lastName') }
          data-test='input-lastname'
          form={ form }
          disabled={ isSomeCasesSubmitted }
        />
      </Flex>
      <Flex gap='4'>
        <FormInput
          name='email'
          label={ t('clients.formLabels.email') }
          data-test='input-email'
          form={ form }
          disabled={ isSomeCasesSubmitted }
        />
        <FormInput
          name='mobile'
          label={ t('clients.formLabels.phoneNumber') }
          data-test='input-mobile'
          form={ form }
          type='phone'
          disabled={ isSomeCasesSubmitted }
        />
      </Flex>
      <Flex gap='4'>
        <FormSelectInput
          name='citizen_status'
          label={ t('clients.formLabels.residencyStatus') }
          form={ form }
          data={ getResidencyType() }
          disabled={ isSomeCasesSubmitted }
        />
        <FormSelectInput
          name='employment_status'
          label={ t('clients.formLabels.employmentStatus') }
          form={ form }
          data={ getEmploymentType() }
          disabled={ isSomeCasesSubmitted }
        />
      </Flex>
      <Flex gap='4'>
        <FormInput
          name='dob'
          label={ t('clients.formLabels.dob') }
          data-test='select-main-dob'
          form={ form }
          type='date'
          disabled={ isSomeCasesSubmitted }
        />
        <FormInput
          name='salary'
          label={ t('clients.formLabels.fixedMonthlySalary') }
          data-test='fixed-month-salary'
          type='number'
          form={ form }
          trail={ OPPORTUNITY_CURRENCY.AED }
          disabled={ isSomeCasesSubmitted }
        />
      </Flex>
      {businessUnit === BUSINESS_IDENTIFIER.AE_HUSPY && (
        <FormSelectInput
          name='nationality'
          label={ t('clients.formLabels.nationality') }
          data={ NATIONALITIES }
          form={ form }
          disabled={ isSomeCasesSubmitted }
        />
      )}
      {businessUnit === BUSINESS_IDENTIFIER.ES_BAYTECA && (
        <>
          <Flex gap='4'>
            <FormInput
              name='employment_start_date'
              data-test='select-employment-start-date'
              label={ t('clients.formLabels.employmentStartDate') }
              type='date'
              form={ form }
              disabled={ isSomeCasesSubmitted }
            />
            <FormInput
              name='no_of_annual_employment_payments'
              label={ t('clients.formLabels.noOfAnnualPayments') }
              data-test='no-of-annual-payments'
              type='number'
              form={ form }
              disabled={ isSomeCasesSubmitted }
            />
          </Flex>
          <Flex gap='4'>
            <Box id='has_a_bonus' w='50%'>
              <Radio
                data-test='has-bonus'
                name='has_a_bonus'
                label={ t('clients.formLabels.receivesBonus') }
                data={ getYesNo() }
                defaultValue={ form.getValues().has_a_bonus ? 'yes' : 'no' }
                onValueChange={ ({ value }) => {
                  form.setFieldValue('has_a_bonus', value === 'yes');
                } }
                disabled={ isSomeCasesSubmitted }
              />
            </Box>
          </Flex>
          {form.getValues().has_a_bonus && (
            <Flex gap='4'>
              <FormSelectInput
                name='bonus_frequency'
                data-test='bonus-frequency'
                label={ t('clients.formLabels.bonusType') }
                form={ form }
                data={ getBonusTypes() }
                disabled={ isSomeCasesSubmitted }
              />
              <FormInput
                name='bonus_value'
                data-test='bonus-value'
                label={ t('clients.formLabels.bonusValue') }
                type='number'
                form={ form }
                trail={ OPPORTUNITY_CURRENCY.EUR }
                disabled={ isSomeCasesSubmitted }
              />
            </Flex>
          )}
        </>
      )}

    </>
  );
};
